// SettingsPage — centered content column with a left "On this page" scroll-spy
// rail (ported pattern from UtilityDesk's Thesis/Intelligence views). Each
// section is expanded (non-collapsible) so the scroll-spy has real targets.
//
// SKU Categories moved to the Inventory page (the office now manages
// rename / reorder / markup / delete from there).

import { useEffect, useRef, useState } from 'react';
import { Building2, DollarSign, Hash, MessageSquare, Settings as SettingsIcon, Users } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card.js';
import { ScrollNavRail, type SectionDef } from '../components/ScrollNavRail.js';
import { CompanyInfoSection } from '../components/CompanyInfoSection.js';
import { JobNumberingSection } from '../components/JobNumberingSection.js';
import { LaborRatesSection } from '../components/LaborRatesSection.js';
import { PeopleManagementSection } from '../components/PeopleManagementSection.js';
import { FeedbackSection } from '../components/FeedbackSection.js';

// Module scope → stable reference, so the rail's IntersectionObserver effect
// (deps [sections, scrollRoot]) attaches once instead of rebuilding per render.
const SETTINGS_SECTIONS: SectionDef[] = [
  { id: 'company', label: 'Company' },
  { id: 'labor-rates', label: 'Labor Rates' },
  { id: 'job-numbering', label: 'Job Numbering' },
  { id: 'people', label: 'People & Crews' },
  { id: 'feedback', label: 'Feedback' },
  { id: 'coming-later', label: 'Coming later' },
];

export function SettingsPage() {
  // The scroll container is AppShell's <main overflow-auto>; this page renders
  // as its child and doesn't own it, so find it once after mount for the
  // rail's IntersectionObserver root.
  const rootRef = useRef<HTMLDivElement>(null);
  const [scrollRoot, setScrollRoot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setScrollRoot(rootRef.current?.closest('main') ?? null);
  }, []);

  return (
    <div ref={rootRef} className="pt-6">
      {/* Content centered on the page (equal 1fr gutters); the rail is
          centered within the LEFT gutter, the right gutter is an empty
          symmetric spacer. */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_minmax(0,870px)_1fr]">
        {/* Left rail — centered in the left gutter (hidden below lg) */}
        <div className="hidden lg:flex lg:justify-center">
          <ScrollNavRail sections={SETTINGS_SECTIONS} scrollRoot={scrollRoot} />
        </div>

        {/* Content column — capped at 870px by the grid track */}
        <div className="w-full space-y-8">
          <section id="company" className="scroll-mt-4 space-y-3">
            <h2 className="eyebrow flex items-center gap-1.5">
              <Building2 className="h-3 w-3" />
              Company
            </h2>
            <CompanyInfoSection />
          </section>

          <section id="labor-rates" className="scroll-mt-4 space-y-3">
            <h2 className="eyebrow flex items-center gap-1.5">
              <DollarSign className="h-3 w-3" />
              Labor Rates
            </h2>
            <LaborRatesSection />
          </section>

          <section id="job-numbering" className="scroll-mt-4 space-y-3">
            <h2 className="eyebrow flex items-center gap-1.5">
              <Hash className="h-3 w-3" />
              Job Numbering
            </h2>
            <JobNumberingSection />
          </section>

          <section id="people" className="scroll-mt-4 space-y-3">
            <h2 className="eyebrow flex items-center gap-1.5">
              <Users className="h-3 w-3" />
              People & Crews
            </h2>
            <PeopleManagementSection />
          </section>

          <section id="feedback" className="scroll-mt-4 space-y-3">
            <h2 className="eyebrow flex items-center gap-1.5">
              <MessageSquare className="h-3 w-3" />
              Feedback
            </h2>
            <FeedbackSection />
          </section>

          <section id="coming-later" className="scroll-mt-4 space-y-3">
            <h2 className="eyebrow flex items-center gap-1.5">
              <SettingsIcon className="h-3 w-3" />
              Coming later
            </h2>
            <Card className="border-dashed">
              <CardContent className="p-6">
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>
                    <span className="font-medium text-foreground">Bot configuration</span> —
                    Telegram bot token + webhook
                  </li>
                </ul>
              </CardContent>
            </Card>
          </section>
        </div>

        {/* Symmetric spacer keeps the content visually centered */}
        <div className="hidden lg:block" />
      </div>
    </div>
  );
}
